import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { API, auth, inputCls, PrimaryBtn, errMsg } from './finance/shared';
import Icon from '../../components/dashboard/DashboardIcons';
import { TICKET_STATUS_LABELS, TICKET_PRIORITY_LABELS, TICKET_CATEGORY_LABELS } from './SupportPage';
import { toast } from '../../lib/toast';

const fmtDateTime = (iso) => { if (!iso) return ''; try { return new Date(iso).toLocaleString('ar-SA'); } catch { return iso; } };
const CLOSED_STATUSES = ['resolved', 'closed'];

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="support-rating-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`support-rating-star-${n}`}>
        <Icon name="star" fill={n <= value ? 'currentColor' : 'none'} className={`h-6 w-6 ${n <= value ? 'text-amber-400' : 'text-slate-200'}`} />
      </button>
    ))}
  </div>
);

export default function SupportTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/support-tickets/${id}`, auth())
      .then((res) => { setTicket(res.data.ticket); setMessages(res.data.messages || []); })
      .catch((e) => setError(errMsg(e, 'تعذّر تحميل المذكرة')))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(load, [load]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support-tickets/${id}/messages`, { message: reply.trim() }, auth());
      setReply('');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'فشل إرسال الرد'));
    } finally {
      setSending(false);
    }
  };

  const submitRating = async () => {
    setRatingSubmitting(true);
    try {
      await axios.post(`${API}/support-tickets/${id}/rating`, { rating: ratingValue, comment: ratingComment.trim() || undefined }, auth());
      toast.success('شكراً لتقييمك');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'فشل إرسال التقييم'));
    } finally {
      setRatingSubmitting(false);
    }
  };

  if (loading) {
    return <div className="mx-auto max-w-3xl space-y-3" data-testid="support-detail-loading">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }
  if (error || !ticket) {
    return (
      <div className="mx-auto max-w-3xl rounded-xl border border-red-100 bg-red-50 p-6 text-center text-sm text-red-700" dir="rtl" data-testid="support-detail-error">
        {error || 'المذكرة غير موجودة'}
        <div className="mt-3"><button onClick={() => navigate('/support')} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700">رجوع للدعم</button></div>
      </div>
    );
  }

  const canRate = CLOSED_STATUSES.includes(ticket.status) && !ticket.rating;

  return (
    <div className="mx-auto max-w-3xl space-y-4" dir="rtl" data-testid="support-detail-page">
      <button onClick={() => navigate('/support')} data-testid="support-detail-back-btn" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <Icon name="chevronLeft" className="h-4 w-4 rotate-180" /> رجوع لمذكراتي
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-xs font-mono text-slate-400" dir="ltr">{ticket.ticket_number}</div>
        <h1 className="mt-1 text-lg font-bold text-slate-900" data-testid="support-detail-subject">{ticket.subject}</h1>
        <p className="mt-1 whitespace-pre-line text-sm text-slate-600" data-testid="support-detail-description">{ticket.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TICKET_STATUS_LABELS[ticket.status]?.cls}`} data-testid="support-detail-status-badge">{TICKET_STATUS_LABELS[ticket.status]?.text}</span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${TICKET_PRIORITY_LABELS[ticket.priority]?.cls}`}>{TICKET_PRIORITY_LABELS[ticket.priority]?.text}</span>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{TICKET_CATEGORY_LABELS[ticket.category] || ticket.category}</span>
          <span className="text-xs text-slate-400">أُنشئت {fmtDateTime(ticket.created_at)}</span>
          {ticket.assigned_to_name && <span className="text-xs text-slate-400">· المسؤول: {ticket.assigned_to_name}</span>}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-800">المحادثة</div>
        {messages.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-400" data-testid="support-detail-no-messages">لا توجد ردود بعد</p>
        ) : (
          <div className="space-y-3 p-4">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_type === 'admin' ? 'justify-start' : 'justify-end'}`} data-testid="support-message-bubble">
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender_type === 'admin' ? 'bg-slate-100 text-slate-800' : 'bg-[#16233b] text-white'}`}>
                  <p className="whitespace-pre-line">{m.message}</p>
                  <p className={`mt-1 text-[11px] ${m.sender_type === 'admin' ? 'text-slate-400' : 'text-slate-300'}`}>{m.sender_name} · {fmtDateTime(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={sendReply} className="flex items-end gap-2 border-t border-slate-100 p-3">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="اكتب ردك هنا..."
            rows={2}
            data-testid="support-reply-input"
            className={`${inputCls} flex-1`}
          />
          <PrimaryBtn type="submit" disabled={sending || !reply.trim()} data-testid="support-reply-submit">{sending ? 'جارٍ الإرسال...' : 'إرسال'}</PrimaryBtn>
        </form>
      </div>

      {canRate && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="support-rating-box">
          <p className="mb-2 text-sm font-bold text-slate-800">قيّم تعاملنا مع مذكرتك</p>
          <StarPicker value={ratingValue} onChange={setRatingValue} />
          <textarea
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
            placeholder="تعليق اختياري..."
            rows={2}
            data-testid="support-rating-comment"
            className={`${inputCls} mt-3`}
          />
          <button onClick={submitRating} disabled={ratingSubmitting} data-testid="support-rating-submit" className="mt-3 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-50">
            {ratingSubmitting ? 'جارٍ الإرسال...' : 'إرسال التقييم'}
          </button>
        </div>
      )}
      {ticket.rating && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="support-rating-readonly">
          <p className="mb-2 text-sm font-bold text-slate-800">تقييمك لهذه المذكرة</p>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => <Icon key={n} name="star" fill={n <= ticket.rating ? 'currentColor' : 'none'} className={`h-5 w-5 ${n <= ticket.rating ? 'text-amber-400' : 'text-slate-200'}`} />)}
          </div>
          {ticket.rating_comment && <p className="mt-2 text-sm text-slate-600">{ticket.rating_comment}</p>}
        </div>
      )}
    </div>
  );
}
